import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import axios from 'axios';
import { BASE_URL, GET_RETURN_DRAFT, APPROVE_RETURN, DELETE_DRAFT_ITEM } from '../utlilities/config';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FiPrinter } from 'react-icons/fi';
import { MdDelete } from 'react-icons/md';
import Swal from 'sweetalert2';

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading...</p>
    </div>
  </div>
);

const ReturnAproval = () => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [generalsettings, setGeneralSettings] = useState({});
  const token = localStorage.getItem('accessToken');

  const getItemUniqueKey = (item) => {
    return [
      item?.Sequence ?? '',
      item?.InvNo ?? '',
      item?.SI_No ?? '',
      item?.ItemNo ?? '',
      item?.SupplierID ?? '',
      item?.BatchCode ?? '',
      item?.PurchaseRefNo ?? '',
      item?.PurchaseDate ?? '',
      item?.InvoiceDate ?? '',
      item?.ExpiryDate ?? ''
    ].join('|');
  };

  const isElectron = () => {
    return window && window.process && window.process.type;
  };

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  useEffect(() => {
    getReturnDraftList();
    fetchGeneralSettings();
  }, []);

  const fetchGeneralSettings = async () => {
    try {
      let generalSetting;
      if (isElectron()) {
        generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
      }
      setGeneralSettings(generalSetting || {});
    } catch (error) {
      console.error('Error fetching general settings:', error);
    }
  };

  // Extract unique suppliers from details
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [];
    const seen = new Set();
    details.forEach(item => {
      if (!seen.has(item.SupplierID)) {
        seen.add(item.SupplierID);
        uniqueSuppliers.push({ id: item.SupplierID, name: item.SupplierName });
      }
    });
    return uniqueSuppliers;
  }, [details]);

  // Filter items by selected supplier
  const filteredItems = useMemo(() => {
    if (!selectedSupplier) return [];
    const supplierId = Number(selectedSupplier);
    return details.filter(item => Number(item.SupplierID) === supplierId);
  }, [details, selectedSupplier]);

  const getReturnDraftList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_RETURN_DRAFT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        const data = response.data.data;
        setDetails(data.details || []);
      }
    } catch (error) {
      toast.error('Failed to fetch return draft list.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
    setSelectedItems([]);
  };

  const handleItemSelect = (item) => {
    const key = getItemUniqueKey(item);
    setSelectedItems(prev => {
      if (prev.some(i => getItemUniqueKey(i) === key)) {
        return prev.filter(i => getItemUniqueKey(i) !== key);
      }
      return [...prev, item];
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(filteredItems);
    } else {
      setSelectedItems([]);
    }
  };

  const isItemSelected = (item) => {
    const key = getItemUniqueKey(item);
    return selectedItems.some(i => getItemUniqueKey(i) === key);
  };

  // Calculate total price for an item
  // Formula: (qty * PurchasePrice) + tax (TaxCode%) - discount (applied on amount after tax)
  // If DiscountPercentage > 0, use percentage on (base + tax), else use DiscountAmount
  const calculateItemTotal = (item) => {
    const qty = parseFloat(item.Qty || 0);
    const purchasePrice = parseFloat(item.PurchasePrice || 0);
    const taxCode = parseFloat(item.TaxCode || 0);
    const discountPercentage = parseFloat(item.DiscountPercentage || 0);
    const discountAmount = parseFloat(item.DiscountAmount || 0);

    // Base amount = Qty * PurchasePrice
    const baseAmount = qty * purchasePrice;

    // Tax = Base amount * (TaxCode / 100)
    const tax = baseAmount * (taxCode / 100);

    // Amount after tax = Base + Tax
    const amountAfterTax = baseAmount + tax;

    // Discount: If DiscountPercentage > 0, apply on amount after tax, else use DiscountAmount
    let discount = 0;
    if (discountPercentage > 0) {
      // Apply discount percentage on amount after tax
      discount = amountAfterTax * (discountPercentage / 100);
    } else {
      discount = discountAmount;
    }

    // Total = Amount after tax - Discount
    const total = amountAfterTax - discount;

    return {
      baseAmount,
      tax,
      discount,
      total: Math.max(0, total) // Ensure non-negative
    };
  };

  // Helper function to convert number to words (Indian Format)
  const numToWords = (n) => {
    if (n === 0) return "Zero Rupees Only";

    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const convert = (num) => {
      if (num < 20) return units[num];
      if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "");
      if (num < 1000) return units[Math.floor(num / 100)] + " Hundred" + (num % 100 ? " " + convert(num % 100) : "");
      if (num < 100000) return convert(Math.floor(num / 1000)) + " Thousand" + (num % 1000 ? " " + convert(num % 1000) : "");
      if (num < 10000000) return convert(Math.floor(num / 100000)) + " Lakh" + (num % 100000 ? " " + convert(num % 100000) : "");
      return convert(Math.floor(num / 10000000)) + " Crore" + (num % 10000000 ? " " + convert(num % 10000000) : "");
    };

    const numStr = parseFloat(n).toFixed(2);
    const parts = numStr.split('.');
    const rupees = parseInt(parts[0]);
    const paise = parseInt(parts[1] || 0);

    let words = '';
    if (rupees === 0) {
      words = 'Zero';
    } else {
      words = convert(rupees);
    }

    words = words.trim() + ' Rupees';
    if (paise > 0) {
      words += ' and ' + convert(paise).trim() + ' Paise';
    }

    return words + ' Only';
  };

  // Format expiry date to MM/YY format
  const formatExpiryDateMMYY = (expiryDate) => {
    if (!expiryDate) return '';
    try {
      const date = new Date(expiryDate);
      if (isNaN(date.getTime())) return expiryDate;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${year}`;
    } catch {
      return expiryDate;
    }
  };

  // Format date to DD/MM/YYYY
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const generatePDF = () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one item to generate PDF.', { autoClose: 1000 });
      return;
    }
    console.log(generalsettings)
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      // Get first item for supplier and header info
      const firstItem = selectedItems[0];

      // Calculate totals
      let totalBase = 0;
      let totalTax = 0;
      let totalCGST = 0;
      let totalSGST = 0;
      let totalDiscount = 0;

      // Map values for header
      const firmName = generalsettings?.phShowFirmname
        ? `${generalsettings?.FirstName || ''} ${generalsettings?.LastName || ''}`.trim().toUpperCase()
        : '';
      const address = [
        generalsettings?.AddressLine1,
        generalsettings?.AddressLine2,
      ].filter(Boolean).join(', ');
      const phoneNo = [generalsettings?.MobileNo1, generalsettings?.MobileNo2].filter(Boolean).join(', ');
      const gstNo = generalsettings?.GstNo || '';

      // Supplier details
      const supplierName = firstItem.SupplierName || '';
      const supplierAddress = ''; // Not available in data

      // Document details
      const debitNoteNo = firstItem.InvNo || '';
      const debitNoteDate = formatDateDDMMYYYY(firstItem.EntryDate || new Date());

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;

      // Define table columns
      const tableColumn = [
        "No",
        "Particulars",
        "Batch",
        "Expiry",
        "Qty",
        "Purchase Ref",
        "Purchase Date",
        "Rate",
        "Disc %",
        "Disc Amt",
        "Tax %",
        "Tax Amount",
        "CGST",
        "SGST",
        "Amount"
      ];

      const tableRows = [];

      selectedItems.forEach((item, index) => {
        const calculated = calculateItemTotal(item);
        totalBase += calculated.baseAmount;
        totalTax += calculated.tax;
        totalDiscount += calculated.discount;

        // Calculate CGST and SGST (assuming equal split)
        const taxCode = parseFloat(item.TaxCode || 0);
        const cgst = calculated.tax / 2;
        const sgst = calculated.tax / 2;
        totalCGST += cgst;
        totalSGST += sgst;

        const qty = parseFloat(item.Qty || 0);
        const purchasePrice = parseFloat(item.PurchasePrice || 0);
        const mrp = purchasePrice; // Using purchase price as MRP for return

        const discountPercentage = parseFloat(item.DiscountPercentage || 0);

        tableRows.push([
          index + 1,
          item.ItemName || '',
          item.BatchCode || '',
          formatExpiryDateMMYY(item.ExpiryDate),
          qty.toFixed(0),
          item.PurchaseRefNo || '',
          formatDateDDMMYYYY(item.PurchaseDate || item.InvoiceDate || ''),
          parseFloat(purchasePrice).toFixed(2),
          discountPercentage > 0 ? `${discountPercentage}%` : '0%',
          parseFloat(calculated.discount).toFixed(2),
          `${taxCode}%`,
          parseFloat(calculated.tax).toFixed(2),
          parseFloat(cgst).toFixed(2),
          parseFloat(sgst).toFixed(2),
          parseFloat(calculated.total).toFixed(2)
        ]);
      });

      // Calculate grand total
      const grandTotal = totalBase + totalTax - totalDiscount;
      const amountInWords = numToWords(grandTotal);

      // Header height (adjusted for supplier details)
      const headerHeight = 190;
      const footerHeight = 110; // Increased for extra total line

      // Draw header
      let y = 30;

      // Firm Name
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('DEBIT NOTE', pageWidth / 2, y, { align: 'center' });
      y += 20;

      // Firm details
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(firmName, pageWidth / 2, y, { align: 'center' });
      y += 15;

      if (address) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const addressLines = doc.splitTextToSize(address, pageWidth - 300);
        addressLines.forEach(line => {
          doc.text(line, pageWidth / 2, y, { align: 'center' });
          y += 10;
        });
      }

      if (phoneNo) {
        y += 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(phoneNo, pageWidth / 2, y, { align: 'center' });
        y += 10;
      }

      if (gstNo) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`GSTIN: ${gstNo}`, pageWidth / 2, y, { align: 'center' });
        y += 15;
      }

      // Store y position before splitting into left/right sections
      const headerCenterBottom = y;

      // Supplier details section (left side)
      let leftY = headerCenterBottom + 10;
      const leftStartX = margin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('To : ' + supplierName, leftStartX, leftY);
      leftY += 12;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      if (supplierAddress) {
        doc.text(supplierAddress, leftStartX, leftY);
        leftY += 12;
      }

      // Document details (right side)
      const rightStartX = pageWidth - margin;
      let rightY = headerCenterBottom + 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(`No: ${debitNoteNo}`, rightStartX, rightY, { align: "right" });
      rightY += 12;
      doc.text(`Date: ${debitNoteDate}`, rightStartX, rightY, { align: "right" });
      rightY += 12;

      // Determine table start Y (max of left or right section + padding)
      const tableStartY = Math.max(leftY, rightY) + 5;

      // Calculate available width for table (A4 landscape: 842pt width, minus margins)
      const availableWidth = pageWidth - (margin * 2);

      // Column width ratios must sum to 1.0 to avoid right-side overflow/cutoff
      const columnWidthRatios = [
        0.03, // No
        0.20, // Particulars
        0.07, // Batch
        0.05, // Expiry
        0.04, // Qty
        0.08, // Purchase Ref
        0.07, // Purchase Date
        0.06, // Rate
        0.04, // Disc %
        0.06, // Disc Amt
        0.04, // Tax %
        0.06, // Tax Amount
        0.05, // CGST
        0.05, // SGST
        0.10, // Amount
      ];
      const columnAlignments = [
        'center', 'left', 'left', 'center', 'center', 'left', 'center',
        'right', 'center', 'right', 'center', 'right', 'right', 'right', 'right',
      ];
      const columnOverflow = [
        'linebreak', 'linebreak', 'linebreak', 'linebreak', 'linebreak',
        'ellipsize', 'linebreak', 'linebreak', 'linebreak', 'linebreak',
        'linebreak', 'linebreak', 'linebreak', 'linebreak', 'linebreak',
      ];
      const columnStyles = columnWidthRatios.reduce((styles, ratio, index) => {
        styles[index] = {
          halign: columnAlignments[index],
          cellWidth: availableWidth * ratio,
          overflow: columnOverflow[index],
        };
        return styles;
      }, {});

      const amountColumnLeft =
        margin + availableWidth * columnWidthRatios.slice(0, 14).reduce((sum, ratio) => sum + ratio, 0);
      const tableRightEdge = pageWidth - margin;

      // Draw table with full width and proper styling
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: tableStartY,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 3,
          textColor: [0, 0, 0],
          font: 'helvetica',
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
          overflow: 'linebreak',
        },
        headStyles: {
          fontStyle: 'bold',
          fillColor: [220, 220, 220],
          textColor: [0, 0, 0],
          halign: 'center',
          valign: 'middle',
          cellPadding: 4,
          fontSize: 7,
          lineColor: [0, 0, 0],
          lineWidth: 0.5
        },
        bodyStyles: {
          cellPadding: 3,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          valign: 'middle'
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        columnStyles,
        margin: { top: 20, bottom: footerHeight, left: margin, right: margin },
        tableWidth: availableWidth,
      });

      // Get final Y position after table
      const finalY = doc.lastAutoTable.finalY + 10;

      // Draw footer section
      // Check if we need a new page for footer if there isn't enough space
      if (finalY + 100 > pageHeight - margin) {
        doc.addPage();
        // Reset Y for new page
      }

      // Calculate footer start Y
      // We'll place it after the table, but ensure it's at least at the bottom if the table is short? 
      // User requested "alignment", usually "Amount in words" is fixed at bottom or just after table.
      // The image suggests a fixed footer area at the bottom.

      const footerStartY = pageHeight - 110; // Fixed footer area, slightly taller for discount row

      // Draw line above footer
      doc.setLineWidth(0.5);
      doc.line(margin, footerStartY, pageWidth - margin, footerStartY);

      let footerY = footerStartY + 15;

      // Left side - Amount in words
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Amount in words:', margin, footerY);

      footerY += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      // Auto-wrap amount in words
      const wordsLines = doc.splitTextToSize(amountInWords, pageWidth / 2);
      doc.text(wordsLines, margin, footerY);

      // "For [Firm Name]"
      // Position this lower, near the bottom
      const signY = footerStartY + 85;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('For ' + firmName, margin, signY);

      // Right side - Totals aligned with the Amount column
      const totalsLabelX = amountColumnLeft - 10;
      const totalsValueX = tableRightEdge;

      let totalsY = footerStartY + 15;

      // Total Amount
      doc.setFont('helvetica', 'bold');
      doc.text('Total Amount:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(totalBase).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      totalsY += 15;

      // Total Discount
      if (totalDiscount > 0) {
        doc.text('Total Discount:', totalsLabelX, totalsY, { align: 'right' });
        doc.text(parseFloat(totalDiscount).toFixed(2), totalsValueX, totalsY, { align: 'right' });
        totalsY += 15;
      }

      // Total Tax
      doc.text('Total Tax:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(totalTax).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      totalsY += 10;

      // Divider line for grand total
      doc.setLineWidth(0.5);
      doc.line(totalsLabelX - 70, totalsY, totalsValueX, totalsY);

      totalsY += 12;

      // Grand Total
      doc.setFontSize(11);
      doc.text('Grand Total:', totalsLabelX, totalsY, { align: 'right' });
      doc.text(parseFloat(grandTotal).toFixed(2), totalsValueX, totalsY, { align: 'right' });

      // Authorised Signatory
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Authorised Signatory', totalsValueX, signY, { align: 'right' });

      // Save PDF
      doc.save(`DebitNote_${debitNoteNo}_${debitNoteDate.replace(/\//g, '-')}.pdf`);

      toast.success('PDF generated successfully!', { autoClose: 1000 });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF.', { autoClose: 2000 });
    }
  };

  const handleDeleteItem = async (item) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Delete Item?',
      text: `Are you sure you want to delete "${item.ItemName}" from the return draft?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2 mr-2',
        popup: 'rounded-xl shadow-xl'
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}${DELETE_DRAFT_ITEM}`, {}, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'invoiceNo': item.InvNo || 0,
          'itemNo': item.ItemNo || 0,
          'siNo': item.SI_No || 0,
        },
      });

      if (response.status === 200) {
        toast.success('Item deleted successfully', { autoClose: 1500 });
        // Remove item from selected items if it was selected
        const deletedKey = getItemUniqueKey(item);
        setSelectedItems(prev => 
          prev.filter(i => getItemUniqueKey(i) !== deletedKey)
        );
        // Refresh the list
        getReturnDraftList();
      } else {
        throw new Error(response.data?.message || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        error.message ||
        'An error occurred while deleting the item.';
      toast.error(errorMessage, { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (selectedItems.length === 0) {
      toast.warning('Please select at least one item to approve.', { autoClose: 1000 });
      return;
    }

    // Use first item for header data
    const firstItem = selectedItems[0];

    // Calculate totals for header (sum of all selected items)
    let totalBase = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    // Build details array with all selected items
    const details = selectedItems.map((item, index) => {
      const calculated = calculateItemTotal(item);
      totalBase += calculated.baseAmount;
      totalTax += calculated.tax;
      totalDiscount += calculated.discount;

      return {
        siNo: index + 1,
        itemNo: item.ItemNo,
        itemName: item.ItemName,
        batchCode: item.BatchCode,
        quantity: parseFloat(item.Qty || 0),
        price: parseFloat(item.PurchasePrice || 0),
        total: parseFloat(calculated.total.toFixed(2)),
        taxCode: item.TaxCode || "0",
        taxAmount: parseFloat(calculated.tax.toFixed(2)),
        draftRefNo: item.InvNo || 0,
        departmentID: localStorage.getItem('department_id') || '',
      };
    });

    // Format invoice date (use first item's date)
    const invoiceDate = firstItem.EntryDate || new Date().toISOString().split('T')[0];
    const formattedDate = invoiceDate.includes('T') ? invoiceDate.split('T')[0] : invoiceDate;

    // Build single payload with all items
    const requestPayload = {
      header: {
        supplierID: firstItem.SupplierID || 0,
        supplierName: firstItem.SupplierName || "",
        invoiceDate: formattedDate,
        purchaseRefNo: firstItem.PurchaseRefNo || "",  // First item's purchase ref
        total: parseFloat((totalBase + totalTax - totalDiscount).toFixed(2)),
        taxAmount: parseFloat(totalTax.toFixed(2)),
        remarks: ""
      },
      details: details
    };

    console.log('Payload to send:', requestPayload);

    setLoading(true);
    try {
      // Send all items in one API call
      const response = await axios.post(`${BASE_URL}${APPROVE_RETURN}`, requestPayload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Sequence': firstItem.Sequence || 0,
          'InvoiceNo': firstItem.InvNo || 0,
          'EditNo': 0,
          'DocStatus': 'O'
        },
      });

      if (response.status !== 200) {
        setLoading(false);
        throw new Error('Failed to approve items');
      }

      toast.success('Items approved successfully!', { autoClose: 1000 });
      generatePDF();
      setTimeout(() => {
        setSelectedItems([]);
        getReturnDraftList();
      }, 500);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Approve error:', error);
      toast.error('Failed to approve items.', { autoClose: 1000 });
    }
  };

  return (
    <div className='flex h-full bg-background'>
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {loading && <Spinner />}
      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>
        <div className="bg-white p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-black flex items-center">
                Return Approval
              </h1>
            </div>
          </div>
        </div>

        {/* Supplier Select */}
        <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3">
          <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
            <h3 className="font-semibold text-black-800 text-sm uppercase tracking-wide">
              Filter by Supplier
            </h3>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-black-600 uppercase mb-1">
              Supplier
            </label>
            <select
              value={selectedSupplier}
              onChange={handleSupplierChange}
              className="w-full md:w-1/3 p-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">-- Select Supplier --</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Items Table */}
        <div className="mt-3 flex-1 overflow-hidden flex flex-col ml-3 mr-3">
          <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
            <h3 className="font-semibold text-black text-sm">Return Items</h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-400 flex-1 w-full">
            <div className="overflow-y-auto overflow-x-auto border rounded-xl relative bg-white h-full">
              <table className="border-collapse text-xs w-full" style={{ minWidth: '900px' }}>
                <thead className="bg-gray-200 text-left sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 rounded"
                        disabled={filteredItems.length === 0}
                      />
                    </th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Inv No</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '200px' }}>Item Name</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Batch</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Qty</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Expiry</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Purchase Ref</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Unit Price</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Total</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap text-center" style={{ minWidth: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item, index) => (
                      <tr
                        key={getItemUniqueKey(item)}
                        className={`border-b border-gray-300 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-indigo-50 transition-colors`}
                      >
                        <td className="px-3 py-2 text-sm text-black border border-gray-200">
                          <input
                            type="checkbox"
                            checked={isItemSelected(item)}
                            onChange={() => handleItemSelect(item)}
                            className="h-4 w-4 text-indigo-600 rounded"
                          />
                        </td>
                        <td className="px-3 py-2 text-sm text-black border border-gray-200">{item.InvNo}</td>
                        <td className="px-3 py-2 text-sm text-black font-medium border border-gray-200">{item.ItemName}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border border-gray-200">{item.BatchCode}</td>
                        <td className="px-3 py-2 text-sm text-black border border-gray-200 text-right">{item.Qty}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border border-gray-200">{item.ExpiryDate}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 border border-gray-200">{item.PurchaseRefNo}</td>
                        <td className="px-3 py-2 text-sm text-black border border-gray-200 text-right">₹{parseFloat(item.PurchasePrice || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-sm text-black font-bold border border-gray-200 text-right">
                          ₹{calculateItemTotal(item).total.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-sm border border-gray-200 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item);
                            }}
                            className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                            title="Delete item"
                          >
                            <MdDelete size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="px-3 py-8 text-center text-gray-500 border border-gray-200">
                        {selectedSupplier ? 'No items found for this supplier.' : 'Please select a supplier to view items.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="bg-white ml-3 mr-3 mt-3 mb-2 rounded-lg shadow-sm border border-gray-300">
          <div className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {/* Selected Count */}
              <div className="flex items-center gap-2 border-r border-gray-300 pr-4">
                <span className="text-xs font-semibold text-black">Selected Items:</span>
                <span className="text-base font-bold text-indigo-600">{selectedItems.length}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={selectedItems.length === 0}
                  className={`px-4 py-2 font-medium text-sm rounded-md shadow-sm transition-all ${selectedItems.length > 0
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700'
                    : 'bg-gray-400 text-white cursor-not-allowed'
                    }`}
                >
                  Approve Selected ({selectedItems.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnAproval;